import { Loader, Select, Stack, Text } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useGetDeviceDetails } from '@/queries/deviceQueries'
import { getDeviceGpus, isJetsonDevice } from '@/utils/device/getDeviceAttr'
import { getOfferedGpus, getReadableGpus } from '../../helpers/gpuSelection'
import { FormValues } from '../DeploymentSettings'
import { SettingsCard } from '../SettingsCard'
import { IntegratedGpuNotice } from './IntegratedGpuNotice'

type GpuSettingsProps = {
  deviceIds: string[]
  savedVlmGpus?: number[]
  form: UseFormReturnType<FormValues>
}

export const GpuSettings = ({
  deviceIds,
  savedVlmGpus,
  form
}: GpuSettingsProps) => {
  const intl = useIntl()

  const gpuIndex = form.getValues().gpuIndex
  const isMultiDevice = deviceIds.length > 1

  const { data: device, isLoading } = useGetDeviceDetails(
    deviceIds[0],
    !isMultiDevice && deviceIds.length > 0
  )

  // Auto-select the first offered GPU when the device loads, replacing one that is no longer offered.
  // gpuIndex and form are intentionally omitted from deps to avoid overwriting manual selection.
  useEffect(() => {
    if (
      !device ||
      isMultiDevice ||
      isJetsonDevice(device) ||
      getDeviceGpus(device).length === 0
    ) {
      return
    }

    const offeredIndexes = getOfferedGpus(device, savedVlmGpus).map(
      (gpu) => gpu.index
    )

    if (gpuIndex === null || !offeredIndexes.includes(gpuIndex)) {
      form.setFieldValue('gpuIndex', offeredIndexes[0] ?? null)
    }
  }, [device, savedVlmGpus]) // eslint-disable-line react-hooks/exhaustive-deps

  const renderContent = () => {
    if (deviceIds.length === 0) {
      return (
        <Text size="sm" c="dimmed">
          <FormattedMessage id="deployments.gpuSettings.noDeviceSelected" />
        </Text>
      )
    }

    if (isMultiDevice) {
      return (
        <Text size="sm" c="dimmed">
          <FormattedMessage id="deployments.gpuSettings.multiDeviceNotSupported" />
        </Text>
      )
    }

    if (isLoading) {
      return <Loader size="sm" />
    }

    if (!device) {
      return null
    }

    if (isJetsonDevice(device)) {
      return <IntegratedGpuNotice />
    }

    const gpus = getReadableGpus(device)

    if (gpus.length === 0) {
      return (
        <Text size="sm" c="dimmed">
          <FormattedMessage id="devices.gpu.unavailable" />
        </Text>
      )
    }

    const offeredGpus = getOfferedGpus(device, savedVlmGpus)
    const reservedCount = gpus.length - offeredGpus.length

    if (offeredGpus.length === 0) {
      return (
        <PlatformAlert variant="error" compact>
          <FormattedMessage id="deployments.gpuSettings.allReserved" />
        </PlatformAlert>
      )
    }

    const gpuOptions = offeredGpus.map((gpu) => ({
      value: String(gpu.index),
      label: intl.formatMessage(
        { id: gpu.name ? 'devices.gpu.indexedName' : 'devices.gpu.indexLabel' },
        { index: gpu.index, name: gpu.name }
      )
    }))

    return (
      <Stack gap={4}>
        <Select
          value={gpuIndex !== null ? String(gpuIndex) : null}
          data={gpuOptions}
          allowDeselect={false}
          onChange={(val) =>
            form.setFieldValue('gpuIndex', val !== null ? Number(val) : null)
          }
        />

        {reservedCount > 0 && (
          <Text size="xs" c="dimmed">
            <FormattedMessage
              id="deployments.gpuSettings.reservedHidden"
              values={{ count: reservedCount }}
            />
          </Text>
        )}
      </Stack>
    )
  }

  return (
    <SettingsCard
      title={<FormattedMessage id="deployments.gpuSettings.title" />}
      description={
        <FormattedMessage id="deployments.gpuSettings.description" />
      }
    >
      {renderContent()}
    </SettingsCard>
  )
}
